import { isAxiosError } from 'axios';
import request from '../utils/axios/request';
import resetFormat from '../utils/reset';
import { Query } from '../utils/types/query';
import { PaginationContent } from '../utils/types/pagination';
import { IYard } from './Yard.service';
import { ClientPermissions, IClient, QueryClient } from './Client.service';
import { ICarrier } from './Carrier.service';

interface QueryUser extends QueryClient {
	account?: string;
}

export interface UserPermissions extends ClientPermissions {
	isSystemAdmin: boolean;
	isLocalAdmin: boolean;
}

export interface IUser {
	id?: number;
	name: string;
	phone: string;
	email: string;
	cpf: string;
	role?: string;
	isActive: boolean;
	shouldReceiveAlerts: boolean;
	acessoFluxoUnico: boolean;
	yards: Partial<IYard>[];
	carriers?: Partial<ICarrier>[];
	client?: Partial<IClient>;
	actions?: string;
	permissions: UserPermissions;
	password?: string;
	type?: 'User' | 'Client' | 'Shipowner';
}

abstract class UserService {
	static async get(id: number) {
		try {
			const data = await request(`/users/${id}`, 'get');
			return data as IUser;
		} catch (error: any) {}
	}

	static async getAll(params?: QueryUser) {
		let isActive, account;
		if (params?.isActive && Number(params.isActive) > -1) {
			isActive = params.isActive;
		}

		account = params?.account ?? 'User';

		try {
			const response: PaginationContent<IUser> = await request(
				'/users',
				'get',
				{
					params: { ...params, isActive, account },
				}
			);
			return response;
		} catch (error: any) {}
	}

	static async create(
		data: Partial<IUser>,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'post');
	}

	static async edit(
		data: Partial<IUser>,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'patch');
	}

	private static async createOrEdit(
		data: Partial<IUser>,
		setError: React.Dispatch<React.SetStateAction<string>>,
		mode: 'post' | 'patch'
	) {
		try {
			const { id, yards, carriers, client, ...user } = data;
			const cpf = resetFormat(user.cpf || '');
			const idYards = yards?.map((item) => item.id);
			const idCarriers = carriers?.map((item) => item.id);
			const url = mode === 'post' ? '/users' : `/users/${id}`;
			const response = await request(url, mode, {
				data: {
					...user,
					cpf,
					idYards,
					idCarriers,
					id_cliente: client?.id,
				},
			});
			return response;
		} catch (error) {
			if (isAxiosError(error) && error.status === 409)
				setError('alert.error.email');
			else setError('alert.error.generic');
		}
	}

	static async delete(id: number) {
		const response = await request(`/users/${id}`, 'delete');
		return response;
	}

	static async getClient() {
		try {
			const response: IClient = await request(`/users/client`, 'get');
			return response;
		} catch (error: any) {}
	}
}

export default UserService;
